import { useCallback, useEffect, useRef, useState } from 'react'
import { useHistory } from 'react-router-dom'
import styled from 'styled-components'

import {
	Button,
	Checkbox,
	DatePicker,
	Form,
	FormInstance,
	Input,
	notification,
	Radio,
	Select,
	Tag,
	Tooltip,
	Typography,
} from 'antd'
import { CloseOutlined, PlusOutlined, DeleteFilled } from '@ant-design/icons'
import { FieldData } from 'rc-field-form/lib/interface'

import * as MunicipalityService from '../../../services/bb-api/municipality'
import * as StatusService from '../../../services/bb-api/status'

import { AddressSearchBar, Address } from '../../shared/address-search-bar'
import { DeleteModal } from '../../shared/delete-modal'
import { useDeviceSize } from '../../../hooks/device-size'
import { ImageSelector } from '../../shared/image-selector'

import { txt } from '../../../locales/i18n'
import * as Municipalities from '../../../context/municipalities'

const handleSelectionFilter = (search: string, options: any) => {
	const searchLowerCased = search.toLowerCase()
	const labelLowerCased = options.label.toLowerCase()
	return labelLowerCased.startsWith(searchLowerCased)
}

export type EventFormProps = {
	initialValues?: any
	onSubmit: (event: any) => void | Promise<void>
	submitButtonText: string
	onRemove?: () => void | Promise<void>
}

export const EventForm = (props: EventFormProps) => {
	const [isLoading, setIsLoading] = useState(true)
	const [showDeleteModal, setShowDeleteModal] = useState(false)
	const [imageChanged, setImageChanged] = useState(false)
	const [imageSource, setImageSource] = useState<string | null>(props.initialValues?.imageSource ?? null)

	const [municipalityVisibleSearch, setMunicipalityVisibleSearch] = useState('')
	const [municipalityVisible, setMunicipalityVisible] = useState<{ label: string; value: number }[]>([])

	const [statuses, setStatuses] = useState<{ name: string; id: number }[]>([])
	const [municipalities, setMunicipalities] = useState<{ label: string; value: number }[]>([])

	const [plusMembersOnly, setPlusMembersOnly] = useState(false)

	const isMobileDevice = useDeviceSize()
	const formRef = useRef<FormInstance>(null)
	const history = useHistory()

	const getFormData = useCallback(async () => {
		setIsLoading(true)

		const statuses = await StatusService.query()
		setStatuses(statuses)

		const municipalities: any[] = await MunicipalityService.query()
		const municipalitiesReduced = municipalities.reduce((municipalities, { id, name }) => {
			if (id > 1000) return municipalities

			const municipality = { value: id, label: name }
			municipalities.push(municipality)

			return municipalities
		}, [])

		setMunicipalities(municipalitiesReduced)
		setMunicipalityVisible(municipalitiesReduced)

		setIsLoading(false)
	}, [setIsLoading])

	useEffect(() => {
		getFormData()
	}, [getFormData])

	useEffect(() => {
		setPlusMembersOnly(props.initialValues?.plusMembersOnly)
	}, [props.initialValues?.plusMembersOnly]);

	const handleAddressSelect = (address: Address | null) => {
		const form = formRef.current
		if (form === null) return

		if (address === null) {
			const addressFields: FieldData[] = [
				{ name: 'city', value: null },
				{ name: 'address', value: null },
				{ name: 'postalCode', value: null },
				{ name: 'lat', value: null },
				{ name: 'lng', value: null },
			]

			return form?.setFields(addressFields)
		}

		const addressFields: FieldData[] = [
			{ name: 'city', value: address.city },
			{ name: 'address', value: address.name },
			{ name: 'postalCode', value: address.postalCode },
			{ name: 'lat', value: address.lat },
			{ name: 'lng', value: address.lng },
		]

		form?.setFields(addressFields)
	}

	const handleCreateEvent = async (event: any) => {
		const form = formRef.current
		if (form === null) return

		if (imageSource === null) {
			const message = txt('notification.image missing')
			return notification.error({ message })
		}

		if (event.totalTickets !== 0 && event.bookingLink !== '') {
			const message = txt('notification.only booking link or total tickets')
			form.setFields([
				{ name: 'totalTickets', errors: [message] },
				{ name: 'bookingLink', errors: [message] },
			])
			return notification.error({ message })
		}

		if (event.totalTickets === 0 && event.bookingLink === '') {
			const message = txt('notification.booking link or total tickets')
			form.setFields([
				{ name: 'totalTickets', errors: [message] },
				{ name: 'bookingLink', errors: [message] },
			])
			return notification.error({ message })
		}

		const dates = event.dates ?? [null, null]

		if (event.plusMembersOnly === false && (dates[0] === null || dates[1] === null)) {
			const message = txt('notification.date missing')
			return notification.error({ message })
		}

		const municipalities = event.municipalities ?? []

		if (event.plusMembersOnly === false && municipalities.length === 0) {
			const message = txt('notification.municipality missing')
			return notification.error({ message })
		}

		const address = event.address ?? null

		if (event.plusMembersOnly === false && address === null) {
			const message = txt('notification.address missing')
			return notification.error({ message })
		}

		event.imageSource = imageChanged ? imageSource : null

		if (event.totalTickets === 0) {
			event.totalTickets = null
			event.availableTickets = null
		}

		if (event.totalTickets !== 0) {
			event.availableTickets = event.totalTickets
		}

		setIsLoading(true)
		await props.onSubmit(event)
		setIsLoading(false)
	}

	const selectMunicipalities = (type: string) => {
		const form = formRef.current
		if (form === null) return

		if (type === 'all') {
			const municipalityIds = municipalityVisible.map(({ value }) => value)
			return form.setFields([{ name: 'municipalitiesVisible', value: municipalityIds }])
		}

		if (type === 'none') {
			return form.setFields([{ name: 'municipalitiesVisible', value: [] }])
		}

		const region = Municipalities.regions[type] ?? null
		if (region === null) return

		const regionFiltered = region.filter((municipalityId) => {
			return municipalityVisible.some(({ value }) => value === municipalityId)
		})

		const currentSelections = form.getFieldValue('municipalitiesVisible')
		const value = [...currentSelections, ...regionFiltered]

		return form.setFields([{ name: 'municipalitiesVisible', value }])
	}

	const getMunicipalityVisible = async (type: 'all' | 'active' | 'inactive') => {
		setIsLoading(true)
		selectMunicipalities('none')

		const query: any = { country: 'Denmark' }
		if (type !== 'all') query.active = type === 'active'

		try {
			const municipalities: any[] = await MunicipalityService.query(query)

			const municipalitiesReduced = municipalities.reduce((municipalities, { id, name }) => {
				if (id > 1000) return municipalities

				const municipality = { value: id, label: name }
				municipalities.push(municipality)

				return municipalities
			}, [])

			setMunicipalityVisible(municipalitiesReduced)
		} catch (error) {
			const message = txt('notification.unknown error')
			notification.error({ message })
		}

		setIsLoading(false)
	}

	return (
		<>
			<FormStyled
				ref={formRef}
				labelCol={{ span: 5 }}
				onFinish={handleCreateEvent}
				initialValues={props.initialValues ?? {}}
				$isMobileDevice={isMobileDevice}
				$isPlusMembersOnly={plusMembersOnly}
			>
				<div className="input-container">
					<Form.Item label={txt('global.title')} name="title">
						<Input required />
					</Form.Item>

					<Form.Item label={txt('global.text')} name="body">
						<Input.TextArea required autoSize />
					</Form.Item>

					<Form.Item label={txt('global.image')}>
						<ImageSelector
							imageSource={imageSource}
							onSelect={(base64) => {
								setImageChanged(true)
								setImageSource(base64)
							}}
							onReset={() => {
								setImageChanged(false)
								setImageSource(props.initialValues?.imageSource ?? null)
							}}
							onError={() => setImageSource(null)}
						/>
					</Form.Item>

					<Form.Item
						label={txt('global.plus-members')}
						name="plusMembersOnly"
						initialValue={false}
						valuePropName="checked"
					>
						<Checkbox onChange={(event) => setPlusMembersOnly(event.target.checked)}>
							{txt('global.plus-members-only')}
						</Checkbox>
					</Form.Item>

					<Form.Item label={txt('global.dates')} name="dates" className="plus-member-hide">
						<DatePicker.RangePicker
							showTime={{ format: 'HH:mm' }}
							format="YYYY-MM-DD HH:mm"
							allowEmpty={[false, false]}
							allowClear={false}
						/>
					</Form.Item>

					<Form.Item label={txt('global.organizer')} name="organizer">
						<Input required />
					</Form.Item>

					<Form.Item name="bookingLink" label={txt('global.booking link')} initialValue="">
						<Input />
					</Form.Item>

					<Form.Item label={txt('global.price')} name="price">
						<Input type="number" required />
					</Form.Item>

					<Form.Item label={txt('global.municipality')} name="municipalities" className="plus-member-hide">
						<Select
							allowClear
							mode="multiple"
							loading={isLoading}
							placeholder={txt('global.please select')}
							style={{ width: '100%' }}
							options={municipalities}
							filterOption={handleSelectionFilter}
							tagRender={(props) => <Tag color="blue" children={props.label} {...props} />}
						/>
					</Form.Item>

					<Form.Item label={txt('global.search')} className="plus-member-hide">
						<AddressSearchBar onSelect={handleAddressSelect} />
					</Form.Item>

					<Form.Item name="address" label={txt('global.address')} className="plus-member-hide">
						<Input placeholder={txt('global.address')} disabled />
					</Form.Item>

					<Form.Item label={txt('global.city')} className="plus-member-hide">
						<div className="multi-input-container">
							<Form.Item name="postalCode" noStyle>
								<Input disabled placeholder={txt('global.postal code')} className="postalcode-input" />
							</Form.Item>

							<Form.Item name="city" noStyle>
								<Input placeholder={txt('global.city')} disabled />
							</Form.Item>
						</div>
					</Form.Item>

					<Form.Item label="GPS" className="plus-member-hide">
						<div className="multi-input-container">
							<Form.Item name="lat" noStyle>
								<Input disabled placeholder="latitude" />
							</Form.Item>

							<Form.Item name="lng" noStyle>
								<Input disabled placeholder="longitude" />
							</Form.Item>
						</div>
					</Form.Item>

					<Form.Item name="status" label="Status">
						<Select loading={isLoading}>
							{statuses.map((status: any) => (
								<Select.Option key={status.id} value={status.id}>
									{status.name}
								</Select.Option>
							))}
						</Select>
					</Form.Item>

					<Form.Item label={txt('global.total tickets')} name="totalTickets" initialValue={0} className="plus-member-hide">
						<Input
							type="number"
							disabled={props.initialValues?.totalTickets || props.initialValues?.totalTickets === 0}
						/>
					</Form.Item>

					<div className="visible-container">
						<Form.Item label={txt('global.visibility')}>
							<Input
								allowClear
								placeholder={txt('global.search')}
								onChange={({ target }) => setMunicipalityVisibleSearch(target.value)}
							/>

							<Radio.Group defaultValue="all" onChange={({ target }) => getMunicipalityVisible(target.value)}>
								<Radio value="all">{txt('global.all')}</Radio>
								<Radio value="active">{txt('global.active')}</Radio>
								<Radio value="inactive">{txt('global.inactive')}</Radio>
							</Radio.Group>

							<Form.Item name="municipalitiesVisible">
								<Checkbox.Group disabled={isLoading}>
									{municipalityVisible.map((municipality) => {
										const searchNormalized = municipalityVisibleSearch.toLowerCase()
										const labelNormalized = municipality.label.toLowerCase()

										return (
											<Checkbox
												key={municipality.value}
												value={municipality.value}
												style={{ display: labelNormalized.startsWith(searchNormalized) ? 'flex' : 'none' }}
											>
												<Tooltip title={municipality.label}>
													<Typography.Text ellipsis>{municipality.label}</Typography.Text>
												</Tooltip>
											</Checkbox>
										)
									})}
								</Checkbox.Group>
							</Form.Item>

							{municipalityVisibleSearch === '' && (
								<div className="municipality-buttons-container">
									<Button danger size="small" type="link" onClick={() => selectMunicipalities('none')}>
										{txt('municipality-buttons.none')}
									</Button>

									<Button size="small" type="link" onClick={() => selectMunicipalities('all')}>
										{txt('municipality-buttons.all')}
									</Button>

									<Button size="small" type="link" onClick={() => selectMunicipalities('Fyn')}>
										{txt('municipality-buttons.fyn')}
									</Button>

									<Button size="small" type="link" onClick={() => selectMunicipalities('Nordjylland')}>
										{txt('municipality-buttons.north-jytland-short')}
									</Button>

									<Button size="small" type="link" onClick={() => selectMunicipalities('Midt-Vestjylland')}>
										{txt('municipality-buttons.mid-west-jytland-short')}
									</Button>

									<Button size="small" type="link" onClick={() => selectMunicipalities('Midt-Østjylland')}>
										{txt('municipality-buttons.mid-east-jytland-short')}
									</Button>

									<Button size="small" type="link" onClick={() => selectMunicipalities('Sønderjylland')}>
										{txt('municipality-buttons.south-jytland-short')}
									</Button>

									<Button size="small" type="link" onClick={() => selectMunicipalities('Storkøbenhavn')}>
										{txt('municipality-buttons.copenhagen-short')}
									</Button>

									<Button size="small" type="link" onClick={() => selectMunicipalities('Vestegnen')}>
										{txt('municipality-buttons.west-copenhagen')}
									</Button>

									<Button size="small" type="link" onClick={() => selectMunicipalities('Nordsjælland')}>
										{txt('municipality-buttons.north-zealand-short')}
									</Button>

									<Button size="small" type="link" onClick={() => selectMunicipalities('Vestsjælland')}>
										{txt('municipality-buttons.west-zealand-short')}
									</Button>

									<Button size="small" type="link" onClick={() => selectMunicipalities('Sydøstsjælland')}>
										{txt('municipality-buttons.south-east-zealand-short')}
									</Button>
								</div>
							)}
						</Form.Item>
					</div>
				</div>

				<div className="submit-container">
					<Button size={isMobileDevice ? 'large' : 'middle'} type="primary" htmlType="submit" loading={isLoading}>
						{props.submitButtonText} <PlusOutlined />
					</Button>

					<Button size={isMobileDevice ? 'large' : 'middle'} onClick={history.goBack}>
						{txt('global.cancel')} <CloseOutlined />
					</Button>

					{props.onRemove && (
						<Button
							danger
							type="primary"
							size={isMobileDevice ? 'large' : 'middle'}
							onClick={() => setShowDeleteModal(true)}
						>
							{txt('global.delete')} <DeleteFilled />
						</Button>
					)}
				</div>
			</FormStyled>

			{props.onRemove && <DeleteModal onConfirm={props.onRemove} useVisible={[showDeleteModal, setShowDeleteModal]} />}
		</>
	)
}

type FormStyledProps = {
	$isMobileDevice: boolean;
	$isPlusMembersOnly: boolean;
}

const FormStyled = styled(Form)<FormStyledProps>`
	padding: 2rem;
	display: flex;
	flex-direction: column;
	gap: 2em;
	max-width: 720px;
	width: 100%;

	.input-container {
		display: grid;
		gap: 0rem 2rem;
	}

	.ant-upload {
		width: 100%;
		height: 250px;
		background-color: #eee;

		img {
			max-width: 100%;
			max-height: 100%;
			object-fit: contain;
		}
	}

	.ant-picker {
		width: 100%;
	}

	.multi-input-container {
		display: flex;

		> * {
			flex: 1;
		}

		.postalcode-input {
			width: 12ch;
			flex: none;
		}
	}

	.visible-container {
		.ant-radio-group {
			margin-top: 1rem;
			display: flex;
			justify-content: center;
		}

		#municipalitiesVisible {
			display: grid;
			grid-template-columns: repeat(auto-fit, minmax(92px, 1fr));
			gap: 1ch 2ch;
			margin-top: 1rem;

			.ant-checkbox-wrapper,
			.ant-typography {
				margin: 0;
				font-weight: normal;
			}

			.ant-checkbox-wrapper {
				span:last-child {
					overflow-x: hidden;
				}
			}
		}

		.municipality-buttons-container {
			display: flex;
			flex-wrap: wrap;
			gap: 0.2rem 1rem;
			justify-content: center;
		}
	}

	.submit-container {
		display: flex;
		gap: 1rem;
		justify-content: end;
		flex-direction: ${({ $isMobileDevice }) => ($isMobileDevice ? 'column' : 'row')};
		padding-bottom: 2rem;
	}
	
	.plus-member-hide {
		display: ${({ $isPlusMembersOnly }) => ($isPlusMembersOnly ? 'none' : 'flex')};
	}
`
